/**
 * LoginShell — composite login page chrome + form.
 *
 * Supports 3 modes:
 *
 *   - `tenant` (default): subdomain is URL-derived; passkey + MFA supported.
 *   - `cross-domain`: user-input subdomain via <SubdomainPrompt>; rate-limit /
 *     CSRF / credential-persistence / cross-domain redirect opt-ins; for
 *     "marketing entry" or "no-subdomain" login flows.
 *   - `customer`: simpler surface (no passkey, no subdomain); for
 *     customer-facing portals.
 *
 * @module @puffin-core/login-shell/components/LoginShell
 */

import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CompanyContextBanner } from '@puffin-core/auth-shell';
import { BASE_DOMAIN } from '@puffin-core/subdomain-routing';
import {
  LoginForm,
  SIGN_IN_SEAM_PROP_TYPES,
  requiredUnlessWithoutPasswordReset,
} from './LoginForm.jsx';
import { AuthenticatorChallengeFlow } from './AuthenticatorChallengeFlow.jsx';
import { useCompanyConfiguration } from '../hooks/useCompanyConfiguration.js';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';

/**
 * The login page chrome: `auth-layout > main.auth-layout-main > div.auth-content`.
 *
 * Module-private (never exported from index.js). Extracted because those three
 * wrappers put every card child four levels down before the card even started —
 * the only way to reduce measured JSX depth is a real component boundary, and
 * taking the OUTER chrome (rather than the inner card) keeps LoginShell's ~25
 * LoginForm props where they are instead of drilling them through a wrapper.
 *
 * `footerSlot` renders after `children`, inside `.auth-content` — exactly where
 * it sat inline.
 */
/**
 * The "back to home" affordance every login screen carries.
 *
 * BCU-001 trust-but-verify sweep (2026-07-25): NINE apps hand-rolled this as
 * `footerSlot={<Link className="btn btn-link text-sm auth-back-link">&larr; Back
 * to Home</Link>}` — the same six lines, nine times, each with a literal `&larr;`
 * that points the same way in every locale and so points the WRONG way in a
 * right-to-left one. Meanwhile this package already owned a canonical
 * `login.backToHome` string that nobody consumed, and which had the arrow baked
 * into the string itself.
 *
 * The shell owns it now: apps pass `backToHomeHref` and get the chevron, the
 * mirroring and the styling for free. The existing `btn btn-link text-sm
 * auth-back-link` classes are preserved deliberately — the defect was the glyph,
 * not the design, and silently restyling nine auth screens is not a bug fix.
 *
 * @param {Object} props
 * @param {string} props.href - Destination (usually `/`).
 * @param {React.ElementType} props.LinkComponent - Router link.
 * @param {string} props.label - Resolved link text.
 * @returns {React.JSX.Element}
 */
function BackToHomeLink({ href, LinkComponent, label }) {
  return (
    <LinkComponent to={href} className="btn btn-link text-sm auth-back-link">
      {/* The canonical `.rtl-icon` contract from @puffin-core/shared-styles,
          emitted directly rather than via page-shell's <RtlIcon>. The mirroring
          is pure CSS (`[dir='rtl'] .rtl-icon { transform: scaleX(-1) }`), which
          every app already loads — so this gets identical behaviour without
          dragging page-shell (dnd-kit, command-palette, tree-view) into the
          LOGIN bundle, which is the first chunk a user ever downloads. */}
      <span className="rtl-icon" aria-hidden="true">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          focusable="false"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
      </span>
      <span>{label}</span>
    </LinkComponent>
  );
}

BackToHomeLink.propTypes = {
  href: PropTypes.string.isRequired,
  LinkComponent: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
};

function LoginLayout({ mode, embedded, headerSlot, footerSlot, children }) {
  // W12 (LOGIN-CONVERGENCE): a sign-in EMBEDDED in a host page that already has its `<main>` (a tenant
  // storefront, a marketing site) must not add a second main landmark; a standalone sign-in IS its page's.
  const Main = embedded ? 'div' : 'main';
  return (
    <div className="auth-layout" data-testid="login-shell" data-mode={mode}>
      {headerSlot}
      <Main className="auth-layout-main">
        <div className="auth-content">
          {children}

          {footerSlot}
        </div>
      </Main>
    </div>
  );
}

LoginLayout.propTypes = {
  mode: PropTypes.string,
  embedded: PropTypes.bool,
  headerSlot: PropTypes.node,
  footerSlot: PropTypes.node,
  children: PropTypes.node,
};

export function LoginShell({
  appName,
  appKey,
  appSubtitle,
  // W15: the surface's own name for its sign-in, as the h1; the catalog's `login.title` otherwise.
  title,
  mode = 'tenant',
  embedded = false,
  // DTOWNRETAIL-SITE-001 R9: a host whose own page head is the ONE document-title writer (the tenant
  // site's `<TenantPageHead>`) passes false; the shell then writes no `document.title` and restores none.
  ownsDocumentTitle = true,
  companyId,
  companyName,
  companyLogo,
  subdomain,
  auth,
  onSuccess,
  supportsPasskey,
  supportsPIN = false,
  pinSlot,
  passkeyProps,
  backToHomeHref,
  BackToHomeLinkComponent,
  // The four reset callbacks are REQUIRED — `usePasswordResetClient({ basePath })` spread
  // (`web/require-login-shell-password-reset`). The `passwordResetFlow` prop that once chose a
  // single-shot `simple` flow is retired with that flow, which SIMULATED a sent reset
  // (UNREACHABLE-MODULE-BURNDOWN-001 L16).
  onPasswordResetInitiate,
  onPasswordResetVerify,
  onPasswordResetComplete,
  onPasswordResetResend,
  accessRequestEndpoint,
  showCompanyLinks,
  footerSlot,
  headerSlot,
  subdomainPromptSlot,
  rateLimitConfig,
  csrfConfig,
  subdomainConfig,
  credentialPersistenceConfig,
  redirectConfig,
  onAuthEvent,
  passwordResetCaptchaAction,
  unlockMode = false,
  unavailableMethods,
  // AUTHENTICATOR-CHALLENGE-FANOUT (2026-08-29). Supply it and the shell handles the whole
  // push-2FA screen: `{ pollStatus, onApproved, onUseCode? }`.
  //   - `pollStatus(challengeId)` — the app's own client for
  //     `GET /v1/auth/authenticator/status/:challengeId`. The shell never builds a URL, because
  //     the mount differs per app.
  //   - `onApproved(payload)` — the approved poll's body, carrying the tokens the server minted
  //     when it CONSUMED the challenge. The app installs the session and navigates.
  //   - `onUseCode(result)` — optional. Called with the ORIGINAL login result, which still
  //     carries `mfaToken` + `mfaMethods` for a user who has both factors, so the app can hand
  //     off to its existing MFA screen. Absent, the fallback link is not rendered at all — a
  //     dead affordance is worse than none.
  //   - `hasCodeFallback(result)` — supplied beside `onUseCode`: whether that result leaves a code
  //     method to enter. The link renders only when it answers true.
  //
  // Omit it and nothing changes: the shell renders the form exactly as before.
  authenticatorConfig = null,
  t,
  // LOGIN-CONVERGENCE (2026-10-11): the generic sign-in seams, forwarded whole to `<LoginForm>`
  // (`SIGN_IN_SEAM_PROP_TYPES` there documents each). Collected rather than listed so a seam added to
  // the form cannot be forgotten here.
  ...signInSeams
}) {
  // The login result that carried `requiresAuthenticator`. Held HERE rather than inside
  // `<LoginForm>` because the challenge REPLACES the form — a screen swap is the shell's job,
  // and keeping the result whole is what lets "Use code instead" hand the app back its `mfaToken`.
  const [authenticatorChallenge, setAuthenticatorChallenge] = useState(null);

  // No `onUseCode` guard: this handler is only ever HANDED to the flow when
  // `authenticatorConfig.onUseCode` exists (see the render below), so a guard here would be dead
  // code that reads like a real protection — and an unreachable branch that quietly costs a
  // coverage point is how a file starts drifting below its floor.
  // 🔴 Q-AW-7. `onUseCode` reaches the flow ONLY when THIS login leaves a code method. The
  // factory always supplies the callback — it cannot see the login result at config-build time —
  // so gating the affordance on its mere presence offered "Use a code instead" to a push-only user
  // and dropped them back on the sign-in form with no explanation. The result IS in hand here,
  // which is the only place the decision can honestly be made. See the render below.
  // LOGIN-CONVERGENCE: an `mfaToken` is not the test — the tenant sign-in mints one even when the push is the only
  // method it offers. The config's `hasCodeFallback` (the host's `codeChallengeOf`) answers instead.
  const handleUseCode = useCallback(() => {
    const result = authenticatorChallenge;
    setAuthenticatorChallenge(null);
    authenticatorConfig.onUseCode(result);
  }, [authenticatorChallenge, authenticatorConfig]);

  // A denial or an expiry returns the user to the form. Both are terminal for THAT challenge, and
  // the honest next step is "sign in again" — the flow has already told them which happened.
  const handleChallengeEnded = useCallback(() => setAuthenticatorChallenge(null), []);

  // Memoised so the document-title effect below can depend on it honestly. As a
  // bare inline arrow this took a new identity every render, which is why that
  // effect omitted it — and omitting it was a REAL bug: the effect's deps were
  // `[appName, resolvedCompanyName]`, neither of which changes on a LANGUAGE
  // switch, so `document.title` kept rendering the previous language's string.
  // `useLoginTranslate` memoises on the consumer `t` AND on the `login`-namespace
  // translator, whose identity changes on a language switch — so the title effect
  // re-runs exactly when the language changes and at no other time. (Before W2 the
  // only input was the consumer `t`, which is `undefined` at every render site in
  // the ecosystem, so the title never changed language at all.)
  const translate = useLoginTranslate(t);

  // Mode-derived defaults.
  const resolvedSupportsPasskey =
    typeof supportsPasskey === 'boolean' ? supportsPasskey : mode === 'customer' ? false : true;
  const resolvedShowCompanyLinks =
    typeof showCompanyLinks === 'boolean' ? showCompanyLinks : mode === 'customer' ? false : true;

  // Auto-resolve company from subdomain-routing if caller didn't provide name
  // AND we're not in customer mode (customer mode has no subdomain concept).
  const shouldLookupCompany = mode !== 'customer' && !companyName && companyId;
  const { company } = useCompanyConfiguration(shouldLookupCompany ? companyId : null);
  const resolvedCompanyName = companyName || company?.name;
  const resolvedCompanyLogo = companyLogo || company?.logo;
  const resolvedSubdomain = subdomain || company?.subdomain;

  useEffect(() => {
    if (!ownsDocumentTitle) return undefined;
    const title = resolvedCompanyName
      ? `${appName} - ${translate('login.pageTitle')} | ${resolvedCompanyName}`
      : `${appName} - ${translate('login.pageTitle')}`;
    const prev = document.title;
    document.title = title;
    return () => {
      document.title = prev;
    };
  }, [appName, ownsDocumentTitle, resolvedCompanyName, translate]);

  // The banner names a company, so a company NAME is its one gate (Unit B): a bare subdomain is not a company.
  const showCompanyBanner = mode !== 'customer' && Boolean(resolvedCompanyName);
  const showSubdomainPrompt = mode === 'cross-domain' && subdomainPromptSlot;

  return (
    <LoginLayout
      mode={mode}
      embedded={embedded}
      headerSlot={headerSlot}
      footerSlot={
        backToHomeHref && BackToHomeLinkComponent ? (
          <BackToHomeLink
            href={backToHomeHref}
            LinkComponent={BackToHomeLinkComponent}
            label={translate('login.backToHome')}
          />
        ) : (
          footerSlot
        )
      }
    >
      {/* auth-shell's ONE company banner: the decorative logo, and the full host or no address line. */}
      {showCompanyBanner ? (
        <CompanyContextBanner
          name={resolvedCompanyName}
          logo={resolvedCompanyLogo}
          subdomain={resolvedSubdomain}
          domainSuffix={BASE_DOMAIN}
        />
      ) : null}

      <div className="auth-card" data-testid="login-page">
        <h1 className="auth-card-title">{title ?? translate('login.title')}</h1>
        {appSubtitle && <p className="auth-card-subtitle">{appSubtitle}</p>}

        {showSubdomainPrompt ? subdomainPromptSlot : null}

        {authenticatorChallenge && authenticatorConfig ? (
          <AuthenticatorChallengeFlow
            challengeId={authenticatorChallenge.challengeId}
            numberMatch={authenticatorChallenge.numberMatch}
            pollStatus={authenticatorConfig.pollStatus}
            onApproved={authenticatorConfig.onApproved}
            onDenied={handleChallengeEnded}
            onExpired={handleChallengeEnded}
            onUseCode={
              authenticatorConfig.onUseCode &&
              authenticatorConfig.hasCodeFallback(authenticatorChallenge)
                ? handleUseCode
                : undefined
            }
            submitTotpCode={authenticatorConfig.submitTotpCode}
            t={t}
          />
        ) : (
          <LoginForm
            auth={auth}
            appName={appName}
            appKey={appKey}
            companyId={companyId}
            companyName={resolvedCompanyName}
            onSuccess={onSuccess}
            supportsPasskey={resolvedSupportsPasskey}
            supportsPIN={supportsPIN}
            pinSlot={pinSlot}
            passkeyProps={passkeyProps}
            onPasswordResetInitiate={onPasswordResetInitiate}
            onPasswordResetVerify={onPasswordResetVerify}
            onPasswordResetComplete={onPasswordResetComplete}
            onPasswordResetResend={onPasswordResetResend}
            accessRequestEndpoint={accessRequestEndpoint}
            showCompanyLinks={resolvedShowCompanyLinks}
            // Q-UMB-20 (owner ruling 2026-09-24, "safe default + stated opt-out"): an undefined
            // `rateLimitConfig` reaches `useLoginForm`'s `LOGIN_RATE_LIMIT_CONFIG` default, so every
            // shell is throttled unless its page passes a policy of its own or a pinned `null`.
            rateLimitConfig={rateLimitConfig}
            csrfConfig={csrfConfig}
            subdomainConfig={subdomainConfig}
            credentialPersistenceConfig={credentialPersistenceConfig}
            redirectConfig={redirectConfig}
            onAuthEvent={onAuthEvent}
            passwordResetCaptchaAction={passwordResetCaptchaAction}
            unlockMode={unlockMode}
            unavailableMethods={unavailableMethods}
            onAuthenticatorChallenge={authenticatorConfig ? setAuthenticatorChallenge : undefined}
            {...signInSeams}
            t={t}
          />
        )}
      </div>
    </LoginLayout>
  );
}

LoginShell.propTypes = {
  appName: PropTypes.string.isRequired,
  // Registry app key (e.g. 'planner') for the tenant-neutral "Sign in
  // elsewhere" redirect (Login-1). Optional; falls back to current-host root.
  appKey: PropTypes.string,
  appSubtitle: PropTypes.string,
  title: PropTypes.string,
  mode: PropTypes.oneOf(['tenant', 'cross-domain', 'customer']),
  // W12: rendered inside a host page's own `<main>` — the shell then adds no main landmark.
  embedded: PropTypes.bool,
  // DTOWNRETAIL-SITE-001 R9: false when the host page's own head writes `document.title` (ONE writer).
  ownsDocumentTitle: PropTypes.bool,
  companyId: PropTypes.string,
  companyName: PropTypes.string,
  companyLogo: PropTypes.string,
  subdomain: PropTypes.string,
  auth: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  authenticatorConfig: PropTypes.shape({
    pollStatus: PropTypes.func.isRequired,
    onApproved: PropTypes.func.isRequired,
    onUseCode: PropTypes.func,
    // LOGIN-CONVERGENCE: supplied beside `onUseCode` — whether the held result leaves a code method (the host's ONE
    // reading, `codeChallengeOf`).
    hasCodeFallback: PropTypes.func,
    // Q-D-TOTP-VERIFY: present when the host mounts the TOTP fallback route.
    submitTotpCode: PropTypes.func,
  }),
  supportsPasskey: PropTypes.bool,
  supportsPIN: PropTypes.bool,
  pinSlot: PropTypes.node,
  passkeyProps: PropTypes.object,
  // BCU-001: when supplied, the shell renders the canonical RTL-aware
  // "back to home" link itself instead of the app hand-rolling a `footerSlot`.
  backToHomeHref: PropTypes.string,
  BackToHomeLinkComponent: PropTypes.elementType,
  onPasswordResetInitiate: requiredUnlessWithoutPasswordReset,
  onPasswordResetVerify: requiredUnlessWithoutPasswordReset,
  onPasswordResetComplete: requiredUnlessWithoutPasswordReset,
  onPasswordResetResend: requiredUnlessWithoutPasswordReset,
  accessRequestEndpoint: PropTypes.string,
  showCompanyLinks: PropTypes.bool,
  footerSlot: PropTypes.node,
  headerSlot: PropTypes.node,
  subdomainPromptSlot: PropTypes.node,
  rateLimitConfig: PropTypes.object,
  csrfConfig: PropTypes.object,
  subdomainConfig: PropTypes.object,
  credentialPersistenceConfig: PropTypes.object,
  redirectConfig: PropTypes.object,
  onAuthEvent: PropTypes.func,
  passwordResetCaptchaAction: PropTypes.string,
  unlockMode: PropTypes.bool,
  ...SIGN_IN_SEAM_PROP_TYPES,
  /** Methods offered but unavailable now, with their reasons (`LoginForm`). */
  unavailableMethods: PropTypes.arrayOf(
    PropTypes.shape({ method: PropTypes.string.isRequired, reason: PropTypes.string.isRequired }),
  ),
  t: PropTypes.func,
};

export default LoginShell;
